import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Fab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import { TableCellType } from "components/LazyTable";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import SortableTable from "components/SortableTable";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { ResponseOfWizard } from "services/KnowfullyAdminClient";
import WizardUpdateForm from "./WizardUpdateForm";

const WizardDetails = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { registerPromise } = useTrashablePromises();
  const { id } = useParams<{ id: string }>();
  const { wizardsUrl, wizardService } = useGlobalContext();
  const [updateOpen, updateOpenActions] = useBoolean(false);
  const [deleteOpen, deleteOpenActions] = useBoolean(false);
  const { formatMessage: t } = useIntl();

  const { isLoading, isError, data, error } = useQuery<ResponseOfWizard>(
    ["wizard", id],
    () => wizardService.getWizard(id!),
    {
      enabled: !!id,
    }
  );

  const wizard = data?.data;
  useTitle(wizard?.name || "Wizard Details");

  if (!id) return <Navigate to={wizardsUrl()} replace />;
  if (isLoading) return <Loader />;
  if (isError) {
    console.log(error);

    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="wizardLoadError" />
        </Alert>
      </Box>
    );
  }

  const handleDelete = async () => {
    await registerPromise(wizardService.deleteWizard(id));
    queryClient.invalidateQueries("wizards");
    navigate(wizardsUrl());
  };

  if (updateOpen) {
    return (
      <WizardUpdateForm
        wizard={wizard!}
        onWizardUpdated={updateOpenActions.setFalse}
        onCancel={updateOpenActions.setFalse}
      />
    );
  }

  const {
    organizationName,
    inflectionPoint,
    isActive,
    name,
    header,
    footer,
  } = wizard!;

  const rows = [
    {
      title: t({ id: "organization" }),
      component: <span> {organizationName}</span>,
    },
    {
      title: t({ id: "nameLabel" }),
      component: <span>{name}</span>,
    },
    {
      title: t({ id: "isActiveLabel" }),
      component: <span>{isActive ? <CheckIcon /> : <CloseIcon />}</span>,
    },
    {
      title: t({ id: "inflectionPointLabel" }),
      component: <span>{inflectionPoint.name}</span>,
    },

    {
      title: t({ id: "headerTextLabel" }),
      component: <span>{header}</span>,
    },
    {
      title: t({ id: "footerTextLabel" }),
      component: <span>{footer}</span>,
    },
  ].filter((r) => !!r.component);

  const widgets = wizard?.widgets ?? [];

  const cells: Array<TableCellType> = [
    {
      accessor: "name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "widgetNameLabel" }),
    },
    {
      accessor: "codeConstant",
      align: "center",
      width: 0,
      type: "text",
      label: t({ id: "codeConstantLabel" }),
    },
    {
      accessor: "sequence",
      align: "center",
      width: 0,
      type: "text",
      label: t({ id: "sequenceLabel" }),
    },
  ];

  return (
    <>
      <Box my={2}>
        <RedirectBackButton
          redirectRoute={wizardsUrl()}
          labelKey="backToListButtonLabel"
        />
      </Box>
      <Typography variant="h4">{wizard?.name}</Typography>

      <Box my={3} display="flex" justifyContent="center">
        <Fab
          color="primary"
          onClick={updateOpenActions.setTrue}
          data-cy="editButton"
        >
          <EditIcon />
        </Fab>
        <Box ml={4} />
        <Fab
          color="secondary"
          onClick={deleteOpenActions.setTrue}
          data-cy="deleteButton"
        >
          <DeleteIcon />
        </Fab>
      </Box>

      <Box my={3}>
        <TableContainer>
          <Table>
            <TableBody>
              {rows.map(({ title, component }, i) => (
                <TableRow key={i}>
                  <TableCell component="th" scope="row">
                    <b>{title}</b>
                  </TableCell>
                  <TableCell>{component}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <SortableTable
        {...{
          rows: widgets,
          dense: true,
          title: t({ id: "widgetsLabel" }),
          cells,
        }}
        defaultOrderBy="sequence"
        useRowIndexesAsReactKey={true}
      />

      <DeleteEntityDialog
        dialogTitleKey="wizardDeleteLabel"
        contentKey="wizardDeleteInfo"
        onCancel={deleteOpenActions.setFalse}
        handleDelete={handleDelete}
        open={deleteOpen}
      />
    </>
  );
};

export default WizardDetails;
